export const DEFAULT_CODE = `import pandas as pd

def process(file_path, nexla_utils, *args, **kwargs) -> dict:
    """
    Processes files located at the specified file_path.

    Args:
        file_path (str) | (list): The path where the files are accessible.
        nexla_utils: Utility functions provided by Nexla for logging and other operations.
        *args: Positional arguments for future use.
        **kwargs: Keyword arguments for future use.

    Returns:
        dict: A mapping of output file names to their corresponding DataFrame objects or list of records,
              e.g., {
                  '<file_name1>.csv': <DataFrame_Object1>,
                  '<file_name2>.csv': [{'count': 1}, {'count': 2}, {'count': 3}]
              }
    """

    nexla_utils.publish_info_log("Starting process function...")

    # Read the JSON file into a DataFrame
    df = pd.read_json(file_path)

    # Prepare the result dictionary with the output file name and DataFrame
    result = {
        'output.csv': df,
    }

    nexla_utils.publish_info_log("Process function completed.")
    nexla_utils.publish_warning_log("[Warning!] Process function completed.")
    nexla_utils.publish_error_log("[Error!] Process failed")
    return result
`;

export const DEFAULT_CODE_FLEX_FLOW = `import pandas as pd

def process(file_path, nexla_utils, *args, **kwargs) -> dict:
    """
    Processes files located at the specified file_path.

    Args:
        file_path (str or list): The path or list of paths where the files are accessible.
        nexla_utils: Utility functions provided by Nexla for logging and other operations.
        *args: Positional arguments.
        **kwargs: Keyword arguments (e.g., file_type).

    Returns:
        dict: A mapping of output file names to their corresponding DataFrame objects.
              Example:
              {
                  'output.json': <DataFrame_Object>
              }
    """
    nexla_utils.publish_info_log("Starting process function...")

    file_type = kwargs.get('file_type', 'REPLICATION')
    # Whenever Nexla parser processes a file, file_type value will be 'PARSED'.
    # When file_type is 'PARSED', output should be wrapped with 'rawMessage', else flow will not work.
    # Whenever file_type is 'PARSED', the input file will be a JSON file.

    if file_type == "PARSED":
        # Read the JSON file into a DataFrame
        try:
            df = pd.read_json(file_path)
        except ValueError as e:
            nexla_utils.publish_error_log(f"Error reading JSON file: {e}")
            return {"output.json": []}

        if df.empty:
            nexla_utils.publish_warning_log("Input DataFrame is empty.")
            return {'output.json': df}

        # 'rawMessage' contains the file content.
        # 'nexlaMetaData' contains metadata about the file.
        if 'rawMessage' in df.columns:
            raw_message_df = pd.json_normalize(df['rawMessage'])
            df = pd.concat([df.drop(columns=['rawMessage']), raw_message_df], axis=1)

        if 'nexlaMetaData' in df.columns:
            nexla_metadata_df = pd.json_normalize(df['nexlaMetaData'])
            df = pd.concat([df.drop(columns=['nexlaMetaData']), nexla_metadata_df], axis=1)

        # Wrapping each record as {"rawMessage": record} for Nexla processing.
        processed_records = [{"rawMessage": row.to_dict()} for _, row in df.iterrows()]
        df = pd.DataFrame(processed_records)

    else:
        # Read file based on extension.
        # If JSON, use df.read_json; if CSV, use df.read_csv.
        # Defaulting to reading CSV as an example.
        try:
            df = pd.read_csv(file_path)
        except ValueError as e:
            nexla_utils.publish_error_log(f"Error reading CSV file: {e}")
            return {"output.json": []}

        if df.empty:
            nexla_utils.publish_warning_log("Input DataFrame is empty.")
            return {'output.json': df}

        # Add a column to indicate processing status.
        df['processed'] = True

    nexla_utils.publish_info_log("Process function completed.")

    return {'output.json': df}
`;
